import api from './api';

export interface Sensor {
  id: string;
  imei: string;
  name: string;
  lat: number;
  lng: number;
  status: 'good' | 'warn' | 'alert' | 'offline';
  type?: 'soil' | 'weather' | 'pest';
  battery?: number;
  solarCharge?: number;
  /**
   * Voltajes reales del equipo, en voltios (sensores Xiaoma 0x4f/0x50,
   * confirmados en el Apéndice 6.1 del spec V1.4 como "Battery Level" y
   * "Solar Panel Voltage", ambos en mV).
   *
   * `battery` ya trae un porcentaje derivado de `batteryVoltage` con la curva
   * oficial del fabricante (§6.4) — ver batteryPercentFromVolts en el backend.
   * Estos voltajes se conservan igual porque son el dato con el que se
   * diagnostica la celda; el porcentaje es para leer de un vistazo.
   */
  batteryVoltage?: number;
  solarPanelVoltage?: number;
  signal?: number;
  firmware?: string;
  lastReading?: {
    metrics: Record<string, number>;
    receivedAt: string;
  };
}

export async function getSensors(): Promise<Sensor[]> {
  const { data } = await api.get('/devices');
  return data;
}

export async function getWeatherStations(): Promise<Sensor[]> {
  const { data } = await api.get('/devices/weather-stations');
  return data;
}

/** Una lectura del histórico, ya normalizada por el backend. */
export interface HistoryReading {
  depthCm: number | null;
  receivedAt: string | null;
  /** Claves de UI (humidity, temp…) más battery_voltage / solar_panel_voltage. */
  payload: Record<string, number>;
  sampleCount?: number;
}

export interface SensorHistory {
  imei: string;
  deviceLabel: string | null;
  window: { from: string; to: string };
  /** 'raw' hasta 48 h; 'hourly' (agregado) para ventanas más largas. */
  source: 'raw' | 'hourly';
  bucketSeconds: number | null;
  count: number;
  readings: HistoryReading[];
}

/**
 * Histórico REAL del sensor, desde la base.
 *
 * La versión anterior de esta función era código muerto y además apuntaba a
 * `/sensors/history/:imei`, que no existe — la ruta es `/sensors/:imei/history`.
 * Habría dado 404 el día que alguien la llamara.
 *
 * Devuelve las lecturas más recientes primero (ORDER BY … DESC en el backend).
 */
export async function getSensorHistory(
  imei: string,
  opts: { hours?: number; from?: string; to?: string; depth?: number; limit?: number } = {},
): Promise<SensorHistory> {
  const params = new URLSearchParams();
  // `from`/`to` gana sobre `hours`: el modal de sensor elige ventanas de
  // CALENDARIO (agosto completo, la semana del día X), que no se pueden
  // expresar como "las últimas N horas" sin perder los bordes.
  if (opts.from && opts.to) {
    params.set('from', opts.from);
    params.set('to', opts.to);
  } else {
    params.set('hours', String(opts.hours ?? 24));
  }
  if (opts.depth !== undefined) params.set('depth', String(opts.depth));
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  const { data } = await api.get(`/sensors/${encodeURIComponent(imei)}/history?${params}`);
  return data;
}

/**
 * Guarda la ubicación de un device EN EL SERVIDOR.
 *
 * La página de Locations guardaba el arrastre del marcador en localStorage y
 * anunciaba "Changes save automatically". Se guardaba, sí, pero sólo en ese
 * navegador: el resto de la plataforma —el mapa del dashboard, el de suelo,
 * el panel de administración— seguía leyendo la coordenada vieja de la base,
 * y otra persona entrando desde otra computadora veía el sensor en el lugar
 * equivocado. Peor todavía, quien movió el marcador quedaba convencido de
 * haberlo corregido.
 *
 * PATCH /devices/:id lo persiste de verdad (tenant_admin y manager incluidos).
 */
export async function updateDeviceLocation(deviceId: string, lat: number, lng: number): Promise<void> {
  await api.patch(`/devices/${deviceId}`, { lat, lng });
}

/**
 * Guarda el nombre visible de un device EN EL SERVIDOR.
 *
 * Mismo caso que updateDeviceLocation: renombrar un sensor escribía sólo en
 * localStorage y el botón contestaba "✓ Saved". La etiqueta nunca llegaba a
 * la base, así que cualquier otra persona —y el mismo usuario desde otra
 * computadora— seguía viendo el IMEI de quince dígitos donde el dueño del
 * equipo veía "Santa Maria #1".
 */
export async function updateDeviceLabel(deviceId: string, label: string): Promise<void> {
  await api.patch(`/devices/${deviceId}`, { label });
}
